/**
 * NotebookView (Intelligence Studio)
 *
 * EXACT JSX extracted from containers/IntelligenceStudioPage.jsx NOTEBOOK VIEW.
 * Presentational-only; state/handlers live in the container.
 */

import React from 'react';

import {
  CreateNotebookModal,
  DeleteConfirmationModal,
  ResizeHandle,
  SourcesPanel,
  Toast,
  UnifiedHeader,
  MainPanel,
  NotesPanel,
} from '@/pages/intelligence-studio/components';
import NoNotebookSelected from '@/pages/intelligence-studio/views/NoNotebookSelected';
import VaultErrorState from '@/pages/intelligence-studio/views/VaultErrorState';

const NotebookView = ({
  // Layout + data
  notebooks,
  selectedNotebook,
  quotaStatus,
  isLoading,
  error,
  setError,

  // refs + sizing
  containerRef,
  leftWidth,
  rightWidth,
  leftCollapsed,
  rightCollapsed,

  // panel controls
  handleLeftResize,
  handleRightResize,
  toggleLeftCollapse,
  toggleRightCollapse,

  // modal state
  showCreateModal,
  setShowCreateModal,
  handleCreateNotebook,
  deleteConfirmation,
  confirmDelete,
  closeDeleteConfirmation,

  // handlers
  handleBackToHome,
  refreshNotebook,
  handleRenameNotebook,
  handleDeleteNotebook,
  handleSelectNotebook,
  handleFileSelect,
  handleUploadFile,
  handleRemoveDuplicates,
  handleRetryFile,
  handleRemoveFile,
  handleDeleteFile,
  handleRenameFile,
  handleReuploadFile,
  handleReprocessFile,
  handleCitationClick,
  handleBuildTimeline,
  handleMapClauses,

  // state passed to panels
  selectedFile,
  clearSelectedFile,
  targetPage,
  targetText,

  nanoMetadataRows,
  isNanoAnalyzing,
  removeNanoMetadataRow,
  retryNanoMetadataFile,
  clearNanoMetadataRows,

  // notebook state update (kept in container for SRP)
  onUpdateNotebook,
}) => {
  return (
    <div className="h-full w-full flex flex-col bg-white dark:bg-neutral-900">
      {/* ✅ NEW: Unified Header (combines old Header + DocumentHeader) */}
      <UnifiedHeader
        notebook={selectedNotebook}
        quotaStatus={quotaStatus}
        onBack={handleBackToHome}
        onRefresh={refreshNotebook}
        onRename={handleRenameNotebook}
        onDelete={handleDeleteNotebook}
        onNewNotebook={() => setShowCreateModal(true)}
      />

      {/* Main 3-column layout with resizable panels */}
      <div ref={containerRef} className="flex-1 flex min-h-0">
        {/* LEFT: Sources Panel (Collapsible & Resizable) */}
        <div
          style={{
            width: leftWidth,
            minWidth: leftCollapsed ? 48 : 200,
            maxWidth: leftCollapsed ? 48 : 500,
          }}
          className="flex-shrink-0 transition-[width] duration-150"
        >
          <SourcesPanel
            notebooks={notebooks}
            selectedNotebook={selectedNotebook}
            onSelect={handleSelectNotebook}
            onDelete={handleDeleteNotebook}
            onNewNotebook={() => setShowCreateModal(true)}
            collapsed={leftCollapsed}
            onToggleCollapse={toggleLeftCollapse}
            isLoading={isLoading}
            onFileSelect={handleFileSelect}
            selectedFileId={selectedFile?.fileId || selectedFile?.id}
            onUploadFile={handleUploadFile}
            onRemoveDuplicates={handleRemoveDuplicates}
            onRetryFile={handleRetryFile}
            onRemoveFile={handleRemoveFile}
            onDeleteFile={handleDeleteFile}
            onRenameFile={handleRenameFile}
            onReuploadFile={handleReuploadFile}
            onReprocessFile={handleReprocessFile}
            width={leftWidth}
          />
        </div>

        {/* LEFT RESIZE HANDLE */}
        {!leftCollapsed && <ResizeHandle onResize={handleLeftResize} position="left" />}

        {/* MIDDLE: Chat + Overview (Flexible) - no overflow-hidden to allow auto-scroll */}
        <div className="flex-1 flex flex-col min-h-0 min-w-[300px]">
          {/* ✅ ENTERPRISE: Show error state for stuck/failed vaults */}
          {selectedNotebook?._loadError ||
          selectedNotebook?._isStuck ||
          selectedNotebook?.status === 'error' ? (
            <VaultErrorState
              notebook={selectedNotebook}
              onRefresh={refreshNotebook}
              onBack={handleBackToHome}
            />
          ) : selectedNotebook ? (
            <MainPanel
              notebook={selectedNotebook}
              quotaStatus={quotaStatus}
              onRefresh={refreshNotebook}
              onUpdate={onUpdateNotebook}
              onCitationClick={handleCitationClick}
              onRenameNotebook={handleRenameNotebook}
              onDeleteNotebook={handleDeleteNotebook}
              // ✅ NANO-METADATA ENGINE: Pass file DNA rows to chat area
              nanoMetadataRows={nanoMetadataRows}
              isNanoAnalyzing={isNanoAnalyzing} // ✅ ENTERPRISE FIX: Pass upload state
              onRemoveNanoMetadataRow={removeNanoMetadataRow}
              onRetryNanoMetadataRow={retryNanoMetadataFile}
              onClearNanoMetadata={clearNanoMetadataRows}
            />
          ) : (
            <NoNotebookSelected />
          )}
        </div>

        {/* RIGHT RESIZE HANDLE */}
        {!rightCollapsed && <ResizeHandle onResize={handleRightResize} position="right" />}

        {/* RIGHT: File Preview & Graph Panel (Collapsible & Resizable) */}
        <div
          style={{
            width: rightWidth,
            minWidth: rightCollapsed ? 48 : 200,
            maxWidth: rightCollapsed ? 48 : 500,
          }}
          className="flex-shrink-0 transition-[width] duration-150"
        >
          <NotesPanel
            notebook={selectedNotebook}
            collapsed={rightCollapsed}
            onToggleCollapse={toggleRightCollapse}
            selectedFile={selectedFile}
            onClearSelectedFile={clearSelectedFile}
            onBuildTimeline={handleBuildTimeline}
            onMapClauses={handleMapClauses}
            targetPage={targetPage}
            targetText={targetText}
            width={rightWidth}
          />
        </div>
      </div>

      {/* Modals */}
      {showCreateModal && (
        <CreateNotebookModal
          onClose={() => setShowCreateModal(false)}
          onCreate={handleCreateNotebook}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmation.open && (
        <DeleteConfirmationModal
          notebookName={deleteConfirmation.notebookName}
          onConfirm={confirmDelete}
          onCancel={closeDeleteConfirmation}
        />
      )}

      {/* Error toast */}
      {error && <Toast message={error} onClose={() => setError(null)} />}
    </div>
  );
};

export default NotebookView;
